import { DragDropContext, type DropResult } from '@hello-pangea/dnd';
import { ListPlus, Pencil } from 'lucide-react';
import { useMemo, useState } from 'react';
import styled from 'styled-components';

import { confirmPopup } from 'components/Popups/Confirm';

import { Button } from '@/components/ui/button';

import { PlanningMoveRecap } from 'pages/DynamicDeliveryPlanning/components/PlanningMoveRecap';
import { UNASSIGNED_COLOR } from 'pages/DynamicDeliveryPlanning/constants/approColors';

import type { ApproOption } from './ApproPicker';
import { LoosePanel } from './LoosePanel';
import { RoundSection, LOOSE_DROPPABLE_ID, parseTourDroppableId } from './RoundSection';
import { TourMap, type MapDelivery, type MapTour } from './TourMap';
import { DELIVERY_STATES_LABELS } from 'pages/Deliveries/constants/states';
import {
    TASK_STATE_LABELS,
    deliveryHref,
    findStop,
    formatWindow,
    stopWindow,
    deliveryAddressLabel,
    deliveryContractLabel,
    deliveryFullLabel,
    deliveryWindow,
    deliveryZoneName,
    getStopState,
    hasFreefood,
    isTask,
    orderedStops,
    stopAddress,
    stopCoordinates,
    taskLabel,
    tourOrigin,
    type FoundStop,
    type Tour,
    type TourDelivery,
    type TourStopItem,
    type TourTask,
} from '../helpers';
import type { TourActions } from '../hooks/useTourActions';

type Props = {
    tours: Tour[];
    unassignedDeliveries: TourDelivery[];
    unassignedTasks: TourTask[];
    deliveryPersonOptions: ApproOption[];
    actions: TourActions;
    focusedStopId: string | null;
    onFocusStop: (deliveryId: string | null) => void;
    onOpenTask: (task: TourTask) => void;
};

// Flattens a delivery for the map — the only place the delivery graph is projected, and the map's own type.
const mapDelivery = (delivery: TourDelivery) => {
    const { latitude, longitude } = stopCoordinates(delivery) ?? { latitude: 0, longitude: 0 };
    const { start, stop: end } = deliveryWindow(delivery);
    return {
        deliveryId: delivery._id,
        siteName: deliveryFullLabel(delivery),
        latitude,
        longitude,
        address: deliveryAddressLabel(delivery),
        details: [
            hasFreefood(delivery) && 'Freefood',
            delivery.isDynamic && 'Dynamique',
            deliveryZoneName(delivery) && `Zone ${deliveryZoneName(delivery)}`,
            deliveryContractLabel(delivery),
        ]
            .filter(Boolean)
            .join(' · '),
        window: formatWindow(start, end),
        state: DELIVERY_STATES_LABELS[getStopState(delivery)],
        columnCount: delivery.deliveredMicrostoreColumnIds.length,
        serviceMinutes: delivery.theoricServiceTime ?? 0,
    };
};
const mapTask = (task: TourTask): MapDelivery => {
    const { latitude, longitude } = stopCoordinates(task) ?? { latitude: 0, longitude: 0 };
    return {
        deliveryId: task._id,
        siteName: taskLabel(task),
        latitude,
        longitude,
        address: stopAddress(task) ?? 'Adresse inconnue',
        details: task.description,
        window: formatWindow(stopWindow(task).start, stopWindow(task).stop),
        state: TASK_STATE_LABELS[task.state],
        serviceMinutes: task.theoricServiceTime,
        isTask: true,
    };
};

const mapStop = (stop: TourStopItem): MapDelivery[] =>
    stopCoordinates(stop) ? [isTask(stop) ? mapTask(stop) : mapDelivery(stop)] : [];

/**
 * In-place tour editor (mirrors the dynamic planning map view): pick one or more tours on the map (route line
 * or legend) — the selected ones become editable cards on the left (assign appro, drag to reorder, drag a delivery
 * onto another selected tour to move it, or onto « À affecter » to release it). Clicking a delivery marker shows a
 * compact popup with a delivery link.
 */
export const TourEditor = ({
    tours,
    unassignedDeliveries,
    unassignedTasks,
    deliveryPersonOptions,
    actions,
    focusedStopId,
    onFocusStop,
    onOpenTask,
}: Props) => {
    const [selected, setSelected] = useState<Set<string>>(new Set());
    const [isDragActive, setIsDragActive] = useState(false);
    // « Éditer » (same flow as the dynamic planning map): pick deliveries on the map or the cards in
    // click order, then pick where they go — after a delivery (marker) or at the end of a tour (legend).
    const [editMode, setEditMode] = useState(false);
    const [insertMode, setInsertMode] = useState(false);
    const [movingIds, setMovingIds] = useState<string[]>([]);
    const selectionNumberByDeliveryId = useMemo(
        () => new Map(movingIds.map((deliveryId, index) => [deliveryId, index + 1])),
        [movingIds],
    );

    const toggleStop = (stopId: string) => {
        if (!findStop(tours, unassignedDeliveries, unassignedTasks, stopId)) return;
        setMovingIds((current) =>
            current.includes(stopId) ? current.filter((id) => id !== stopId) : [...current, stopId],
        );
    };

    const leaveEditMode = () => {
        setEditMode(false);
        setInsertMode(false);
        setMovingIds([]);
    };

    const movingStops = movingIds.flatMap((stopId) => {
        const found = findStop(tours, unassignedDeliveries, unassignedTasks, stopId);
        return found ? [found] : [];
    });

    const placeIn = async (to: Tour, after: { _id: string; label: string } | null) => {
        if (!movingStops.length) return;
        // The tours losing a stop, deduplicated and excluding the target: what undo has to restore.
        const sources = [
            ...new Map(
                movingStops
                    .flatMap(({ from }) => (from && from._id !== to._id ? [from] : []))
                    .map((tour) => [tour._id, tour]),
            ).values(),
        ];
        const confirmed = await confirmPopup({
            title: 'Déplacer vers un arrêt',
            message: `Déplacer ${movingStops.length} arrêt(s) dans la tournée ${to.name}, ${
                after ? `juste après ${after.label}` : 'en fin de tournée'
            } ?`,
        });
        if (confirmed && (await actions.moveStops(movingStops, to, after?._id ?? null, sources))) leaveEditMode();
    };

    /** Insert mode: the clicked marker is the target — the selection lands right after it, in its tour. */
    const placeAfterStop = async (stopId: string) => {
        if (movingIds.includes(stopId)) return;
        const target = findStop(tours, unassignedDeliveries, unassignedTasks, stopId);
        if (target?.from) await placeIn(target.from, { _id: stopId, label: target.label });
    };

    const releaseMoving = async () => {
        // Group by source tour: one snapshot per tour; stops already loose have nothing to leave.
        const bySource = new Map<string, { from: Tour; stops: FoundStop[] }>();
        for (const found of movingStops) {
            if (!found.from) continue;
            const entry = bySource.get(found.from._id) ?? { from: found.from, stops: [] };
            entry.stops.push(found);
            bySource.set(found.from._id, entry);
        }
        for (const { from, stops } of bySource.values()) await actions.removeMovedStops(stops, from);
        leaveEditMode();
    };

    const toggleTour = (key: string) =>
        setSelected((current) => {
            const next = new Set(current);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });

    const editedTours = tours.filter((tour) => selected.has(tour._id));

    const mapTours = useMemo<MapTour[]>(() => {
        const mapped: MapTour[] = tours.map((tour) => ({
            key: tour._id,
            // The legend is already titled « Tournées » — the name alone, no prefix.
            label: tour.name,
            color: tour.color || UNASSIGNED_COLOR,
            selected: selected.has(tour._id),
            isDynamic: tourOrigin(tour) !== 'fixe',
            deliveries: orderedStops(tour).flatMap(mapStop),
        }));
        // Unassigned deliveries are plotted as their own grey "À affecter" group (markers only, no route) so the
        // dispatcher sees them without selecting anything.
        const looseStops = [...unassignedDeliveries, ...unassignedTasks].flatMap(mapStop);
        if (looseStops.length > 0) {
            mapped.push({
                key: 'unassigned',
                label: 'À affecter',
                color: UNASSIGNED_COLOR,
                selected: false,
                markersOnly: true,
                deliveries: looseStops,
            });
        }
        return mapped;
    }, [tours, selected, unassignedDeliveries, unassignedTasks]);

    const handleDragEnd = async (result: DropResult) => {
        setIsDragActive(false);
        const { source, destination, draggableId } = result;
        if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index))
            return;
        const dragged = findStop(tours, unassignedDeliveries, unassignedTasks, draggableId);
        if (!dragged) return;

        // Dropping on the « À affecter » panel releases the stop from its tour.
        if (destination.droppableId === LOOSE_DROPPABLE_ID) {
            if (dragged.from) await actions.removeStop(draggableId, dragged.label, dragged.from);
            return;
        }

        // Reorder and cross-tour move are the same snapshot operation: place the stop at the drop index, which
        // counts deliveries and tasks alike (the cards list both).
        const target = tours.find((candidate) => candidate._id === parseTourDroppableId(destination.droppableId));
        if (target) await actions.moveStop(dragged.stop, dragged.label, dragged.from, target, destination.index);
    };

    return (
        <DragDropContext onDragStart={() => setIsDragActive(true)} onDragEnd={handleDragEnd}>
            <Layout>
                <Board>
                    {/* Same bar as the dynamic planning's Carte view: « Mode édition » (multi-select), then
                        « Déplacer vers un arrêt… » / « Retirer de la tournée ». */}
                    <Bar>
                        <Button
                            type="button"
                            size="sm"
                            variant={editMode ? 'default' : 'outline'}
                            onClick={() => (editMode ? leaveEditMode() : setEditMode(true))}
                            data-test="tours-map-edit-mode"
                        >
                            <Pencil size={15} />
                            {editMode ? 'Quitter le mode édition' : 'Mode édition'}
                        </Button>
                        {editMode && !insertMode && (
                            <>
                                <Button
                                    type="button"
                                    size="sm"
                                    onClick={() => setInsertMode(true)}
                                    disabled={movingIds.length === 0}
                                    data-test="tours-move-after-delivery"
                                >
                                    Déplacer vers un arrêt…
                                </Button>
                                <Button
                                    type="button"
                                    size="sm"
                                    variant="outline"
                                    onClick={() => void releaseMoving()}
                                    disabled={movingStops.every(({ from }) => !from)}
                                    data-test="tours-release-deliveries"
                                >
                                    Retirer de la tournée
                                </Button>
                                <Count>
                                    {movingIds.length} arrêt{movingIds.length > 1 ? 's' : ''} sélectionné
                                    {movingIds.length > 1 ? 's' : ''}
                                </Count>
                            </>
                        )}
                    </Bar>
                    {insertMode && (
                        <InsertBanner data-test="tours-insert-banner">
                            <InsertBannerText>
                                <ListPlus size={16} />
                                <span>
                                    Cliquez sur l’arrêt cible (carte ou liste) pour y insérer{' '}
                                    <b>{movingIds.length} arrêt(s)</b>, juste après cet arrêt — ou sur une tournée de la
                                    légende pour l’ajouter en fin.
                                </span>
                            </InsertBannerText>
                            <Button type="button" size="sm" variant="outline" onClick={() => setInsertMode(false)}>
                                Annuler
                            </Button>
                        </InsertBanner>
                    )}
                    <PlanningMoveRecap
                        chips={movingStops.map(({ stop, label, from }, index) => ({
                            key: stop.stopId,
                            order: index + 1,
                            approId: stop.stopId,
                            ranks: [],
                            sites: label,
                            appro: from?.name ?? 'À affecter',
                        }))}
                        onRemove={(deliveryId) => toggleStop(deliveryId)}
                    />

                    {selected.size === 0 && !editMode && (
                        <Hint>
                            Sélectionnez une ou plusieurs tournées sur la carte (tracé ou légende) pour les éditer.
                        </Hint>
                    )}

                    {editedTours.map((tour) => (
                        <RoundSection
                            key={tour._id}
                            tour={tour}
                            deliveryPersonOptions={deliveryPersonOptions}
                            isDragActive={isDragActive}
                            onAssignDeliveryPerson={(deliveryPersonUserId) =>
                                void actions.assignDeliveryPerson(tour, deliveryPersonUserId)
                            }
                            stopHref={deliveryHref}
                            selectionNumberByDeliveryId={editMode ? selectionNumberByDeliveryId : undefined}
                            onToggleDelivery={
                                editMode ? (insertMode ? (id) => void placeAfterStop(id) : toggleStop) : onFocusStop
                            }
                            onOpenTask={editMode ? undefined : onOpenTask}
                            focusedDeliveryId={focusedStopId}
                        />
                    ))}

                    <LoosePanel
                        deliveries={unassignedDeliveries}
                        tasks={unassignedTasks}
                        onOpenTask={editMode ? undefined : onOpenTask}
                        onOpen={onFocusStop}
                        selectionNumberByDeliveryId={editMode ? selectionNumberByDeliveryId : undefined}
                        onToggleDelivery={editMode && !insertMode ? toggleStop : undefined}
                        note={
                            selected.size === 0
                                ? 'Sélectionnez une tournée pour y glisser un arrêt.'
                                : 'Glissez un arrêt ici pour le retirer de sa tournée.'
                        }
                    />
                </Board>

                <MapCol>
                    <TourMap
                        tours={mapTours}
                        onToggleTour={toggleTour}
                        focusedDeliveryId={focusedStopId}
                        editMode={editMode}
                        insertMode={insertMode}
                        selectionNumberByDeliveryId={editMode ? selectionNumberByDeliveryId : undefined}
                        onStopClick={(deliveryId) =>
                            insertMode ? void placeAfterStop(deliveryId) : toggleStop(deliveryId)
                        }
                        onPickTour={(key) => {
                            const to = tours.find((tour) => tour._id === key);
                            if (to) void placeIn(to, null);
                        }}
                    />
                </MapCol>
            </Layout>
        </DragDropContext>
    );
};

const Layout = styled.div`
    flex: 1;
    min-height: 0;
    display: flex;
    align-items: stretch;
    gap: 12px;

    @media (max-width: 1100px) {
        flex-direction: column;
    }
`;

const Board = styled.div`
    flex: 0 0 420px;
    min-width: 0;
    max-height: 100%;
    overflow-y: auto;
    display: flex;
    flex-direction: column;
    gap: 10px;

    > * {
        flex-shrink: 0;
    }

    @media (max-width: 1100px) {
        flex: 1 1 auto;
        width: 100%;
        max-height: 420px;
    }
`;

const Bar = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
`;

const Count = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${({ theme }) => theme.textColor};
`;

const InsertBanner = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 10px 14px;
    border: 1px solid ${({ theme }) => theme.ctaPrimaryColor};
    border-radius: 10px;
    background: ${({ theme }) => theme.cardBackgroundColor};
    color: ${({ theme }) => theme.textColor};
    font-size: 13px;
`;

const InsertBannerText = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 8px;

    svg {
        color: ${({ theme }) => theme.ctaPrimaryColor};
        flex-shrink: 0;
    }

    b {
        color: ${({ theme }) => theme.ctaPrimaryColor};
    }
`;

const Hint = styled.div`
    border: 1px dashed ${({ theme }) => theme.lightBorderColor};
    border-radius: 12px;
    padding: 24px 16px;
    text-align: center;
    font-size: 13px;
    color: ${({ theme }) => theme.infoTextColor};
`;

const MapCol = styled.div`
    flex: 1;
    min-width: 0;
    align-self: stretch;
    display: flex;
    min-height: 560px;
`;
